// „Изписване" — ОПЕРАЦИЯТА: начален екран вместо таблица (таблицата е в
// справката „Изписвания"). Незавършеното ми изписване и тези на колегите
// (черновите — секцията я има само когато има какво да се продължи), бързо
// начало (изписване или брак с попълнена причина) и изписаните днес в текущия
// склад; клик на ред отваря документа. „+ Ново" и бързото начало при
// незавършено питат: продължи го или го изтрий. След запис листът се връща
// тук със `state.openDoc` → toast „Виж документа".
import { useEffect, type FC } from "react";
import dayjs from "dayjs";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../hooks/useViewport";
import { useWriteoffs } from "../../../hooks/useInventory";
import type { Writeoff } from "../../../api/inventory";
import { PageHeader } from "../components/list/PageHeader";
import { PRIMARY_BUTTON_CLASS, SECONDARY_BUTTON_CLASS } from "../components/list/buttonStyles";
import { InventoryTheme } from "../components/InventoryTheme";
import { useActionToast } from "../components/useActionToast";
import { useInventoryShell } from "../components/shell/InventoryShellContext";
import { DraftHomeSection } from "../components/drafts/DraftHomeSection";
import { DraftHomeModals } from "../components/drafts/DraftHomeModals";
import { DRAFT_TEXTS } from "../components/drafts/draftTexts";
import { useWriteoffDocument } from "./components/useWriteoffDocument";
import { QuickStartSection, TodayWriteoffsSection } from "./components/home/WriteoffHomeSections";
import { useWriteoffHomeDrafts } from "./components/home/useWriteoffHomeDrafts";
import { WriteoffKindBadge } from "./components/WriteoffKindBadge";
import type { WriteoffPreset } from "./components/writeoffWorksheetShared";

// Layout-ът (Inventory.tsx) подава общите props на всяка страница; тази ги
// чете от useInventoryShell(), затова компонентът не ги взема.
type LayoutProps = {
  onOpenMobileMenu?: () => void;
  isNarrow?: boolean;
  warehouseId?: number | null;
  warehouseName?: string | null;
};

export const WriteoffHomePage: FC<LayoutProps> = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isNarrow } = useViewport();
  const { warehouseId, warehouseName } = useInventoryShell();
  const showToast = useActionToast();
  const writeoffDocument = useWriteoffDocument();
  const drafts = useWriteoffHomeDrafts();

  // „Изписани днес" — само текущият склад (останалите са в справката)
  const today = dayjs();
  const { data: todayRaw = [] } = useWriteoffs(
    warehouseId != null
      ? {
          warehouseId,
          startDate: today.startOf("day").toISOString(),
          endDate: today.endOf("day").toISOString(),
        }
      : null,
  );
  const todayWriteoffs = [...todayRaw].sort((a, b) =>
    (b.createdAt ?? b.date).localeCompare(a.createdAt ?? a.date),
  );

  // Ново изписване (по избор с вид и причина от бързото начало); при
  // незавършено — първо въпросът (`drafts.ask`), изборът стига до листа
  const startNew = (preset?: WriteoffPreset) => drafts.startNew(preset ? { preset } : undefined);

  // След запис: state.openDoc → toast с „Виж документа"
  useEffect(() => {
    const state = location.state as { openDoc?: Writeoff } | null;
    if (!state?.openDoc) return;
    const saved = state.openDoc;
    navigate(location.pathname, { replace: true, state: null });
    drafts.refreshLocal();
    showToast({
      text: t(
        saved.kind === "scrap" ? "inventoryPage.woScrapSavedToast" : "inventoryPage.woSavedToast",
        { number: saved.id },
      ),
      actionLabel: t("inventoryPage.postingViewDocument"),
      onAction: () => writeoffDocument.open(saved, { editable: false }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  return (
    <InventoryTheme>
      <div className="flex h-full flex-col bg-canvas text-ink">
        <PageHeader
          title={t("inventoryPage.writeoff")}
          subtitle={
            [
              warehouseName ? t("inventoryPage.woHomeSubtitle", { warehouse: warehouseName }) : null,
              drafts.mine ? t(DRAFT_TEXTS.writeoff.hasDraft) : null,
            ]
              .filter(Boolean)
              .join(" · ") || undefined
          }
          right={
            <>
              <button
                type="button"
                onClick={() => navigate("/inventory/writeoffs")}
                className={SECONDARY_BUTTON_CLASS}
              >
                {t("inventoryPage.woHomeAll")}
              </button>
              {/* Без склад няма от къде да се изписва (както досега) */}
              <button
                type="button"
                onClick={() => startNew()}
                disabled={!warehouseId}
                className={PRIMARY_BUTTON_CLASS}
              >
                {t("inventoryPage.newWriteoffPlus")}
              </button>
            </>
          }
        />
        <div
          className={`flex min-h-0 flex-1 flex-col gap-[26px] overflow-y-auto ${
            isNarrow ? "px-3 pb-10 pt-3.5" : "px-7 pb-12 pt-5"
          }`}
        >
          <DraftHomeSection
            mine={drafts.mine}
            others={drafts.others}
            texts={DRAFT_TEXTS.writeoff}
            renderBadge={(documentType) => (
              <WriteoffKindBadge kind={documentType === "scrap" ? "scrap" : "writeoff"} />
            )}
            onNew={() => startNew()}
          />
          <QuickStartSection disabled={!warehouseId} onStart={startNew} />
          <TodayWriteoffsSection
            writeoffs={todayWriteoffs}
            onView={(writeoff) => writeoffDocument.open(writeoff)}
          />
        </div>
        <DraftHomeModals drafts={drafts} texts={DRAFT_TEXTS.writeoff} />
        {writeoffDocument.modals}
      </div>
    </InventoryTheme>
  );
};

export default WriteoffHomePage;
